import { Link } from 'react-router-dom'
import HeroCTA from '../components/HeroCTA'
import TrustBadges from '../components/TrustBadges'
import TestimonialSlider from '../components/TestimonialSlider'
import StructuredData from '../components/StructuredData'
import './GeneralContractorsLandingPage.css'

const PHONE_NUMBER = '(541) 658-9374'
const PHONE_HREF = 'tel:+15416589374'

function GeneralContractorsLandingPage() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Laboratory Buildout Support for General Contractors',
    description: 'Blueprint review, project planning, and equipment sourcing for general contractors building laboratory facilities',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Laboratory Specialists'
    },
    areaServed: {
      '@type': 'Country',
      name: 'United States'
    }
  }

  return (
    <div className="gc-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero Section - Problem-aware messaging + dual CTAs */}
      <section className="gc-hero-section">
        <div className="container">
          <div className="gc-hero-content">
            <h1 className="gc-hero-title">
              Won a Lab Buildout Project?
              <span className="gc-hero-accent">Don't Let Specifications Derail Your Schedule</span>
            </h1>
            <p className="gc-hero-subtitle">
              Lab buildouts derail GC projects when equipment doesn't fit, change orders pile up, 
              or compliance gaps cause delays. <strong>We solve these problems.</strong> We catch issues 
              before they become change orders, sequence equipment with your schedule, and deliver 
              lab projects on time and on budget—so you protect your margin and your reputation.
            </p>
            <div className="gc-hero-cta-wrapper">
              <div className="gc-cta-buttons">
                <HeroCTA text="Book Free Project Consultation" variant="accent" />
                <a href={PHONE_HREF} className="gc-phone-cta">
                  <span className="gc-phone-icon" aria-hidden>📞</span>
                  Call {PHONE_NUMBER}
                </a>
              </div>
              <p className="gc-hero-trust-text">
                ✓ Free Consultation • ✓ No Obligation • ✓ Calls returned in under 60 min typically
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Stats - Above the fold credibility */}
      <section className="gc-proof-section">
        <div className="container">
          <div className="gc-proof-stats">
            <div className="gc-proof-stat">
              <div className="gc-stat-number">700+</div>
              <div className="gc-stat-label">Successful Installations</div>
            </div>
            <div className="gc-proof-stat">
              <div className="gc-stat-number">35+</div>
              <div className="gc-stat-label">Years Experience (Founded 1988)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem → Solution Section - Direct mapping */}
      <section className="gc-problems-section">
        <div className="container">
          <h2 className="gc-section-title">Your Lab Buildout Problems—Solved</h2>
          <p className="gc-section-subtitle">
            We've seen what derails lab projects. Here's how we fix it.
          </p>
          <div className="gc-problems-grid">
            <div className="gc-problem-card">
              <div className="gc-problem-label">Your problem</div>
              <h3 className="gc-problem-title">Change orders eating your margin</h3>
              <div className="gc-solution-label">We solve it</div>
              <p className="gc-problem-solution">
                Blueprint review catches lab-specific issues—equipment fit, utility requirements, 
                compliance gaps—before you break ground. No surprises, no costly rework.
              </p>
            </div>
            <div className="gc-problem-card">
              <div className="gc-problem-label">Your problem</div>
              <h3 className="gc-problem-title">Equipment delays holding up trades</h3>
              <div className="gc-solution-label">We solve it</div>
              <p className="gc-problem-solution">
                We understand GC schedules. Equipment and materials arrive when you need them— 
                sequenced to your build, so you're never waiting on lab suppliers.
              </p>
            </div>
            <div className="gc-problem-card">
              <div className="gc-problem-label">Your problem</div>
              <h3 className="gc-problem-title">Lab specs you don't specialize in</h3>
              <div className="gc-solution-label">We solve it</div>
              <p className="gc-problem-solution">
                35+ years of lab expertise. One partner for casework, fume hoods, plumbing, and 
                specialty materials—so you don't juggle multiple vendors or guess at requirements.
              </p>
            </div>
            <div className="gc-problem-card">
              <div className="gc-problem-label">Your problem</div>
              <h3 className="gc-problem-title">Compliance issues shutting you down</h3>
              <div className="gc-solution-label">We solve it</div>
              <p className="gc-problem-solution">
                We know lab codes and requirements. Our planning catches compliance gaps early— 
                so you avoid shutdowns, reinspection delays, and client disputes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - What We Do */}
      <section className="gc-services-section">
        <div className="container">
          <h2 className="gc-section-title">How We Deliver Results</h2>
          <p className="gc-section-subtitle">
            How we eliminate the headaches that come with lab buildouts
          </p>

          <div className="gc-services-grid">
            <div className="gc-service-card">
              <div className="gc-service-icon">📐</div>
              <h3>Blueprint Review</h3>
              <p>
                We catch lab-specific issues before construction—equipment fit, utility requirements,
                compliance gaps—so they never become costly change orders. Your plans are build-ready.
              </p>
            </div>
            <div className="gc-service-card">
              <div className="gc-service-icon">📋</div>
              <h3>Project Planning & Trade Coordination</h3>
              <p>
                Realistic timelines and proper sequencing with HVAC, electrical, plumbing, and mechanical
                trades so lab equipment integrates with your build schedule—no more delays from lab suppliers.
              </p>
            </div>
            <div className="gc-service-card">
              <div className="gc-service-icon">🔧</div>
              <h3>Full-Service Division 11 & 12 Supply</h3>
              <p>
                Complete supply coverage for Division 11 (laboratory equipment) and Division 12 (casework,
                fume hoods, specialty plumbing, and lab-grade materials). Non-brand specific, competitively
                priced, delivered when your schedule requires it.
              </p>
            </div>
            <div className="gc-service-card">
              <div className="gc-service-icon">✅</div>
              <h3>Post-Installation Certification & Safety Testing</h3>
              <p>
                We don't just deliver and walk away. Post-installation certification and safety testing
                ensure everything meets code and performs to spec—so your project closes clean.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us - Outcome-focused */}
      <section className="gc-why-section">
        <div className="container">
          <h2 className="gc-section-title">What You Get When You Partner With Us</h2>
          <p className="gc-section-subtitle">
            Your margin protected. Your schedule on track. Your reputation intact.
          </p>
          <div className="gc-why-grid">
            <div className="gc-why-item">
              <h4>Fewer Change Orders</h4>
              <p>Issues caught before construction—so you avoid surprise costs and protect your budget.</p>
            </div>
            <div className="gc-why-item">
              <h4>One Partner, Not Dozens</h4>
              <p>All lab equipment and materials from a single point of contact—no juggling vendors or chasing suppliers.</p>
            </div>
            <div className="gc-why-item">
              <h4>Lab Expertise You Can Trust</h4>
              <p>35+ years supporting lab buildouts. We've seen every mistake—and how to avoid it.</p>
            </div>
            <div className="gc-why-item">
              <h4>Equipment When You Need It</h4>
              <p>We work to your schedule. Materials arrive when your trades need them—no delays holding up the job.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="gc-trust-section">
        <div className="container">
          <TrustBadges />
        </div>
      </section>

      {/* Testimonials - Social proof from satisfied clients */}
      <section className="gc-testimonials-section">
        <div className="container">
          <h2 className="gc-section-title">Trusted on Lab Projects Like Yours</h2>
          <p className="gc-section-subtitle">
            See how we've helped teams avoid change orders, stay on schedule, and deliver lab projects on budget
          </p>
          <TestimonialSlider />
        </div>
      </section>

      {/* Mid-page CTA bar - Quick action for scrollers */}
      <section className="gc-quick-cta-section">
        <div className="container">
          <div className="gc-quick-cta-content">
            <p className="gc-quick-cta-text">Still have questions? We solve lab buildout problems every day.</p>
            <div className="gc-quick-cta-buttons">
              <a href={PHONE_HREF} className="gc-quick-phone">
                📞 Call {PHONE_NUMBER}
              </a>
              <Link to="/booking" className="gc-quick-book" onClick={scrollToTop}>Book a Free Call</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Dual conversion options */}
      <section className="gc-cta-section">
        <div className="container">
          <div className="gc-cta-content">
            <h2 className="gc-cta-title">Stop Worrying About Lab Specs—Let Us Handle It</h2>
            <p className="gc-cta-text">
              Get a free 15-minute project assessment. We'll identify risks in your plans, show you 
              exactly how we'll solve them, and give you a clear path to on-time, on-budget delivery—no obligation.
            </p>
            <div className="gc-cta-buttons">
              <HeroCTA text="Book Your Free Consultation" variant="accent" />
              <a href={PHONE_HREF} className="gc-phone-cta gc-phone-cta-secondary">
                <span className="gc-phone-icon" aria-hidden>📞</span>
                Or Call {PHONE_NUMBER}
              </a>
            </div>
            <p className="gc-cta-note">
              Calls returned in under 60 minutes typically during business hours; form requests within 24 hours
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}

export default GeneralContractorsLandingPage
